import { ChangeDetectionStrategy, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { PlanCardSelectEvent } from '@app/modules/settings/components/plan-card/plan-card.component';
import { BillingFacade } from '@app/shared/+state/billing';
import { isExpiredSubscription, Plan, PlanId } from '@app/shared/+state/billing/billing.models';
import {
  PageLevelCta,
  PageLevelCtaActionColor,
  PageLevelCtaContext,
} from '@app/shared/components/page-level-cta/page-level-cta.component';
import { LoadingState } from '@app/shared/interfaces';
import { combineLatest } from 'rxjs';
import { filter, map } from 'rxjs/operators';

const LEGACY_MOCK_PLAN = {
  plan: 'Legacy',
  plan_id: 'legacy',
  current_plan: true,
  can_change: true,
  problems: [],
  base_price: null,
  additional_properties: null,
  estimated_price: null,
  prorated_price: null,
  included_properties: 2,
} as unknown as Plan;

@Component({
  standalone: false,
  selector: 'sbnb-select-plan',
  templateUrl: './select-plan.component.html',
  styleUrls: ['./select-plan.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectPlanComponent implements OnInit {
  @Output() resumeSubscription = new EventEmitter<void>();
  @Output() renewSubscription = new EventEmitter<void>();
  public plans$ = combineLatest([this.billingFacade.plans$, this.billingFacade.subscription$]).pipe(
    filter(([plans, subscription]) => Boolean(plans) && Boolean(subscription)),
    map(([plans, subscription]) => {
      if (subscription?.plan?.name !== 'legacy' || isExpiredSubscription(subscription)) {
        return plans;
      }
      return [LEGACY_MOCK_PLAN, ...plans];
    })
  );
  public plansLoading$ = this.billingFacade.plansLoadingState$.pipe(map((state) => state === LoadingState.Pending));
  public subscription$ = this.billingFacade.subscription$;
  public plansError$ = this.billingFacade.plansLoadingState$.pipe(map((state) => state === LoadingState.Error));
  public isAnnualSubscription$ = this.billingFacade.isAnnualSubscription$;
  public cannotSwitchPlans$ = combineLatest([this.isAnnualSubscription$]).pipe(map(([isAnnual]) => isAnnual));
  public isTrialSubscription$ = this.billingFacade.isTrialSubscription$;
  public annualCta: PageLevelCta;

  constructor(
    private readonly billingFacade: BillingFacade,
    private readonly router: Router
  ) {}

  public ngOnInit(): void {
    this.annualCta = {
      title: 'Annual billing plans cannot be changed mid-contract.',
      description: 'Contact sales to review your options.',
      context: PageLevelCtaContext.Neutral,
      actions: [
        {
          text: 'Contact sales',
          color: PageLevelCtaActionColor.Secondary,
          callbackFn: () => {
            // Do nothing, intercom workflow triggered by click on button with id
          },
        },
      ],
    };
  }

  public onSelectPlan({ planId, downgrade, renew, resume }: PlanCardSelectEvent): void {
    if (renew) {
      this.renewSubscription.emit();
      return;
    }

    if (resume) {
      this.resumeSubscription.emit();
      return;
    }

    if (planId === PlanId.Legacy) {
      this.router.navigate(['/settings/subscribe']);
      return;
    }

    this.billingFacade.initiateSubscriptionChange(planId, downgrade);
  }
}
